<template>
  <div class="c-graph c-graph--piechart" ref="canvas">
    <Loading v-if="isLoading" transparent />
    <svg id="chart"></svg>
  </div>
</template>

<script>
import Loading from '@/components/Loading';
import * as d3 from 'd3';

export default {
  props: ['data', 'isLoading'],

  components: { Loading },

  watch: {
    data: {
      handler() {
        this.createChart();
      },
      deep: true
    }
  },

  mounted() {
    this.createChart();
  },

  methods: {
    createChart() {
      // 1. データの準備
      let dataset = {};
      if (this.data.isEmpty) {
        dataset = {
          none: {
            contentsCount: 1,
            ratio: 1
          }
        };
      } else {
        dataset = this.data;
      }

      // グラフ出力用にフォーマット
      let dataset_format = [];

      if (dataset.ctrHigh) {
        dataset_format.push({
          name: 'ctrHigh',
          count: dataset.ctrHigh.contentsCount,
          ratio: dataset.ctrHigh.ratio,
          color: '#3eab7a'
        });
      }
      if (dataset.ctrLow) {
        dataset_format.push({
          name: 'ctrLow',
          count: dataset.ctrLow.contentsCount,
          ratio: dataset.ctrLow.ratio,
          color: '#7acca8'
        });
      }
      if (dataset.rankMiddle) {
        dataset_format.push({
          name: 'rankMiddle',
          count: dataset.rankMiddle.contentsCount,
          ratio: dataset.rankMiddle.ratio,
          color: '#8894ac'
        });
      }
      if (dataset.rankLow) {
        dataset_format.push({
          name: 'rankLow',
          count: dataset.rankLow.contentsCount,
          ratio: dataset.rankLow.ratio,
          color: '#34404e'
        });
      }
      // データがない場合
      if (dataset.none) {
        dataset_format.push({
          name: 'none',
          count: dataset.none.contentsCount,
          ratio: dataset.none.ratio,
          color: '#ECF1F5'
        });
      }

      var width = 250;
      var height = 250;

      // 円グラフの内側・外側半径の設定
      var arc = d3
        .arc()
        .innerRadius(0)
        .outerRadius(Math.min(width, height) / 2 - 1);

      // 円グラフのラベル表示位置を設定
      const radius = (Math.min(width, height) / 2) * 0.57;
      var arcLabel = d3
        .arc()
        .innerRadius(radius)
        .outerRadius(radius);

      // 円グラフ作成用の角度を計算
      var arcs = d3
        .pie()
        .sort(null) // data配列の順番でグラフを作成（未指定の場合は、valueの降順）
        .value(function(d) {
          return d.ratio;
        })(dataset_format);

      // 円グラフ描画エリアを選択
      var svg = d3
        .select('#chart')
        .attr('text-anchor', 'middle')
        .style('font', '12px sans-serif');

      // g要素を追加
      const g = svg
        .append('g')
        .attr('transform', `translate(${width / 2},${height / 2})`);

      // g要素に各データのpath要素を追加
      g.selectAll('path')
        .data(arcs)
        .enter()
        .append('path')
        .attr('fill', d => d.data.color)
        .attr('d', arc)
        .append('title')
        .text(d => {
          if (d.data.name == 'ctrHigh') {
            return `1~5位(クリック率が高い)`;
          } else if (d.data.name == 'ctrLow') {
            return `1~5位(クリック率が低い)`;
          } else if (d.data.name == 'rankMiddle') {
            return `6~50位`;
          } else if (d.data.name == 'rankLow') {
            return `51位以下(データなしを含む)`;
          }
        });

      // データごとにtext要素を設定
      // 値がすごく小さくてグラフの幅を取れない場合、ラベルテキストを非表示にする
      const text = g
        .selectAll('text')
        .data(arcs)
        .enter()
        .append('text')
        .attr('transform', d => {
          // ラベルの位置を調整
          if (d.data.ratio < 1 && d.data.name !== 'none') {
            return `translate(${arcLabel.centroid(d)})`;
          }
        })
        .attr('dy', '0.35em');

      // text要素にデータ名を設定（dataのratioを設定）
      text
        .append('tspan')
        .attr('x', 0)
        .style('font-weight', 'bold')
        .text(d => {
          if (d.data.name == 'none') {
            return '0%';
          } else if (d.data.ratio < 0.1) {
            return '';
          } else {
            return this.percent(d.data.ratio) + '%';
          }
        })
        .attr('fill', d => {
          if (d.data.name !== 'none') {
            return 'white';
          }
        })
        .attr('font-size', '16px');

      // text要素にデータ名を設定（dataのcountを設定）
      text
        .append('tspan')
        .attr('x', 0)
        .attr('y', '1.8em')
        .text(d => {
          if (d.data.name == 'none') {
            return '0件';
          } else if (d.data.ratio < 0.1) {
            return '';
          } else {
            return d.data.count + '件';
          }
        })
        .attr('fill', d => {
          if (d.data.name !== 'none') {
            return 'white';
          }
        })
        .attr('font-size', '10px');
    },
    // 小数点以下の丸め誤差が生じるので%表記に変換する際に小数点以下を切り捨てて丸める
    percent(ratio) {
      return this.$options.filters.roundFloatingPointNumber(ratio * 100, 0);
    }
  }
};
</script>
